import {
	respondTo,
	respondToRange,
	StyledActionListItem,
	StyledButton,
	StyledCheckBox,
	StyledCompactCardSelect,
	StyledContainer,
	StyledDropDown,
	StyledInputContainer,
	StyledTab,
	StyledTabFrame,
	StyledTextBox,
	StyledUnderlineTabList,
	StyledRadioButtons,
	StyledBaseModal,
} from "@animech/ui";
import { css } from "@emotion/react";
import { theme } from "../theme";

export const StyledComponentOverrides = () => css`
	/*
	 * ── CompactCardSelect: vertical layout with dot indicator ──
	 * Restructures the card from horizontal (library default) to vertical
	 * with a small dot as the checked indicator instead of a checkmark.
	 */
	${StyledCompactCardSelect.Root} {
		flex-direction: column;
		height: 100%;
		/* padding: 0 !important; */

		${respondToRange(theme, "mobile", "down")} {
			${StyledCompactCardSelect.Image} {
				/* library limitation: inline background-image style needs !important to override sizing */
				background-size: contain !important;
				background-repeat: no-repeat;
			}
		}

		&[data-color="neutral"] {
			${StyledCompactCardSelect.CheckedIcon} {
				/* library limitation: overriding checked icon appearance (dot vs checkmark) */
				background-color: ${theme.colors.foregrounds[3]} !important;
				bottom: ${theme.dimensions.margin.small};
				top: unset;
				right: unset;
				left: 50%;
				width: 6px;
				height: 6px;

				::before {
					display: none;
				}
			}

			&[data-checked="true"] {
				${StyledCompactCardSelect.CheckedIcon} {
					transform: translateX(-50%);
				}
			}

			${StyledCompactCardSelect.Image} {
				aspect-ratio: 1 / 1;
				margin-right: unset;
				background-size: contain;
				background-repeat: no-repeat;
			}

			${StyledCompactCardSelect.LabelContainer} {
				height: unset;
				margin-top: ${theme.dimensions.margin.xSmall};
				margin-bottom: ${theme.dimensions.margin.small};
				justify-content: flex-end;
				align-items: center;
			}

			${StyledCompactCardSelect.Label} {
				width: 100%;
				text-align: center;
				font-size: 16px;
			}
		}
	}

	/*
	 * ── TextBox stepper: ghost appearance with custom borders ──
	 * Reskins the number input stepper as a bordered group with
	 * transparent buttons and centered text.
	 */
	${StyledTextBox.Root}[data-steppers="true"][data-color="neutral"][data-appearance="ghost"] {
		border: 1px solid ${theme.colors.backgrounds[3].background};
		border-radius: ${theme.dimensions.borderRadius};
		background: transparent;
		overflow: hidden;
		transition: border-color 0.15s ease;

		&:hover {
			border-color: ${theme.colors.foregrounds[5]};
		}

		${StyledTextBox.Container} {
			border: none;
			background: transparent;
		}

		${StyledTextBox.InputContainer} {
			display: flex;
			align-items: center;
		}

		${StyledTextBox.Input} {
			text-align: center;
			font-weight: 300;
			font-size: ${theme.typography.fontSize.base};
			color: ${theme.colors.foregrounds[3]};
			background: transparent;
			border: none;
		}

		${StyledTextBox.StepperDown},
		${StyledTextBox.StepperUp} {
			background: transparent;
			border: none;
			border-radius: 0;
			color: ${theme.colors.foregrounds[3]};
			transition: background-color 0.15s ease;
			min-width: ${theme.components.textBox.stepperWidth};

			&:hover {
				background-color: ${theme.colors.backgrounds[3].background};
			}
		}

		${StyledTextBox.StepperDown} {
			border-right: 1px solid ${theme.colors.backgrounds[3].background};
		}

		${StyledTextBox.StepperUp} {
			border-left: 1px solid ${theme.colors.backgrounds[3].background};
		}

		${StyledTextBox.FocusBorder},
		${StyledTextBox.FocusOutline} {
			display: none;
		}
	}

	/*
	 * ── Tab container padding ──
	 * library limitation: Container has no per-context sizing; Tab wraps
	 * content in a Container whose padding we need to override.
	 */
	${StyledTab.Root} {
		${StyledContainer.Root} {
			padding-top: ${theme.dimensions.margin.xxSmall} !important;
			padding-bottom: ${theme.dimensions.margin.xxSmall} !important;
		}
	}

	/*
	 * ── Mobile touch targets ──
	 * library limitation: Button, CheckBox, DropDown, ActionListItem,
	 * UnderlineTabList, and TabFrame lack mobile-specific theme properties.
	 * Global CSS with !important is required because styled-component
	 * selectors in the library have equal specificity to global rules.
	 */
	${respondTo(theme, "mobile")} {
		${StyledActionListItem.Root},
		button[data-color] {
			min-height: ${theme.app.mobile.minTouchTarget} !important;
			padding: 10px 16px !important;
		}

		${StyledTabFrame.Root} {
			padding-bottom: 8px !important;
		}

		${StyledUnderlineTabList.Root} {
			padding: 0 !important;
		}

		${StyledUnderlineTabList.Item} {
			padding: ${theme.dimensions.margin.xSmall}
				${theme.dimensions.margin.small} !important;
			min-height: 36px !important;
		}

		${StyledTextBox.StepperDown},
		${StyledTextBox.StepperUp} {
			min-width: ${theme.app.mobile.minTouchTarget};
			min-height: ${theme.app.mobile.minTouchTarget};
		}

		${StyledButton.Root} {
			min-height: ${theme.app.mobile.minTouchTarget};
		}

		${StyledCheckBox.Root} {
			min-height: ${theme.app.mobile.minTouchTarget};
		}

		${StyledDropDown.Root} {
			min-height: ${theme.app.mobile.minTouchTarget};
		}

		[data-color] {
			min-height: ${theme.app.mobile.minTouchTarget} !important;
		}

		label[data-color] {
			min-width: ${theme.app.mobile.minTouchTarget} !important;
			min-height: ${theme.app.mobile.minTouchTarget} !important;
		}

		${StyledInputContainer.Root} {
			/* margin-bottom handled via theme: inputContainer.mobileMarginBottom */
			margin-top: 8px !important;
		}

		/*
		 * ── Mobile CompactCardSelect layout ──
		 * Adjusts card dimensions for mobile to allow text wrapping
		 * and remove fixed height constraints.
		 */
		${StyledCompactCardSelect.LabelContainer} {
			width: 120px;
			justify-content: flex-start !important;
		}

		${StyledCompactCardSelect.Label} {
			white-space: normal !important;
		}

		${StyledCompactCardSelect.Root} {
			height: unset !important;
			min-height: unset !important;
		}

		/* 
		 * –– Misc ––
		 */

		${StyledTextBox.InputContainer} {
			display: flex;
		}

		${StyledRadioButtons.Root} {
			${StyledRadioButtons.Item} {
				&:hover {
					${StyledRadioButtons.RadioIcon} {
						box-shadow: rgb(33, 33, 33) 0px 0px 0px 1px inset !important;
					}
				}
			}

			${StyledRadioButtons.RadioIcon} {
				box-shadow: rgb(214, 214, 214) 0px 0px 0px 1px inset !important;
				svg {
					scale: 1.8;
				}
				&::before,
				&::after {
					box-shadow: none !important;
				}
			}
		}
	}

	${StyledBaseModal.Root} {
		${StyledBaseModal.CloseButton} {
			top: ${theme.dimensions.margin.base} !important;
			right: ${theme.dimensions.margin.base} !important;

			& ::before {
				color: #101010 !important;
			}
		}
	}
	[data-ar-root] {
		${StyledBaseModal.Root} {
			${StyledBaseModal.Body} {
				font-size: 16px;
				padding: ${theme.dimensions.margin.large};

				${StyledContainer.Root} {
					padding: 0;
				}

				h3 {
					font-size: 24px;
					font-weight: 300;

					&::before {
						content: "\\0ea49";
						font-family: ${theme.typography.fontFamily.icons}, sans-serif;
						font-style: normal;
						font-weight: normal;
						margin-right: 8px;
						vertical-align: middle;
					}
				}

				${StyledTextBox.Root}[data-focus] {
					${StyledTextBox.FocusBorder},
					${StyledTextBox.FocusOutline} {
						display: none;
					}

					${StyledTextBox.Container} {
						border-color: ${theme.colors.palette.black};
					}
				}

				${StyledTextBox.Container} {
					border: 1px solid;
					border-color: ${theme.colors.palette.grey[400]};
					background-color: ${theme.colors.palette.grey[200]} !important;
					box-shadow: none !important;
				}

				${StyledButton.Root} {
					background-color: ${theme.colors.palette.black} !important;
					box-shadow: none !important;
					color: ${theme.colors.palette.white} !important;
				}
			}
		}
	}
`;
